/**
 * The chord editor for a single keybinding row.
 *
 * Recording is a mode, not a text field: the user presses the chord they want
 * and it is read off the keydown. While that mode is active every keystroke is
 * swallowed, because the chords worth rebinding are exactly the ones the app
 * already answers to — without this, rebinding `mod+j` would toggle the
 * terminal instead of recording.
 *
 * @module
 */
import type { KeyboardEvent } from "react";
import { CircleXIcon } from "lucide-react";

import { formatShortcutLabel } from "../../keybindings";
import { COARSE_POINTER_HIT_AREA_CLASS } from "../../lib/touchTarget";
import { cn } from "../../lib/utils";
import { isMacPlatform } from "../../lib/utils";
import { Button } from "../ui/button";
import { Kbd } from "../ui/kbd";
import { shortcutFromKeyboardEvent, type KeybindingRow } from "./KeybindingsSettings.logic";

export interface KeybindingShortcutRecorderProps {
  readonly row: KeybindingRow;
  readonly isRecording: boolean;
  readonly onStartRecording: () => void;
  readonly onCancelRecording: () => void;
  readonly onRecorded: (shortcut: string) => void;
  /** Injectable so a test can assert both platforms without stubbing globals. */
  readonly platform?: string | undefined;
}

export function KeybindingShortcutRecorder({
  row,
  isRecording,
  onStartRecording,
  onCancelRecording,
  onRecorded,
  platform = typeof navigator === "undefined" ? "" : navigator.platform,
}: KeybindingShortcutRecorderProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    // Everything below happens for every key while recording, including keys we
    // end up ignoring. `settings.tsx` listens on window for Escape and navigates
    // to "/" unless the event is already defaultPrevented, and Forge's own
    // shortcut dispatcher listens on window too — so cancelling a recording
    // would eject the user out of settings, and recording `mod+j` would toggle
    // the terminal. Claim the event before either can see it.
    event.preventDefault();
    event.stopPropagation();

    if (event.key === "Escape" && !event.metaKey && !event.ctrlKey && !event.altKey) {
      onCancelRecording();
      return;
    }

    const recorded = shortcutFromKeyboardEvent(event, { isMac: isMacPlatform(platform) });
    // null means "not a chord yet" — a bare modifier on its way down, or a key
    // with no modifier at all. Stay in recording mode and wait.
    if (recorded) onRecorded(recorded);
  };

  if (isRecording) {
    return (
      <div className="flex items-center gap-1">
        <Button
          autoFocus
          type="button"
          size="xs"
          variant="outline"
          className="min-w-32 justify-center font-mono"
          aria-label={`Recording shortcut for ${row.command}. Press a key combination, or Escape to cancel.`}
          onKeyDown={handleKeyDown}
          onBlur={onCancelRecording}
        >
          Press a shortcut…
        </Button>
        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          // Same treatment as SettingResetButton: stays small visually, takes
          // the 48px touch minimum as invisible pad on coarse pointers only.
          className={cn(
            "text-muted-foreground hover:text-foreground",
            COARSE_POINTER_HIT_AREA_CLASS,
          )}
          aria-label={`Cancel recording shortcut for ${row.command}`}
          // Fires before blur closes the mode, so cancel is reachable by mouse.
          onMouseDown={onCancelRecording}
        >
          <CircleXIcon className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      className="min-w-32 justify-center"
      aria-label={`Change shortcut for ${row.command}, currently ${row.key}`}
      onClick={onStartRecording}
    >
      <Kbd className="px-1.5">{formatShortcutLabel(row.shortcut, platform)}</Kbd>
    </Button>
  );
}
